import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  Trophy,
  Target,
  TrendingUp,
  Users,
  Calendar,
  BookOpen,
  CheckCircle,
  Zap,
  Crown,
} from 'lucide-react';
import { gamificationService } from '../services/gamificationService';
// Interfaces are now imported from gamificationService

export default function StudentGamification() {
  const [activeTab, setActiveTab] = useState('overview')

  // Real API calls
  const { data: pointsData, isLoading: pointsLoading, error: pointsError } = useQuery({
    queryKey: ['student-points'],
    queryFn: () => gamificationService.getStudentPoints(),
  })

  const { data: badges, isLoading: badgesLoading } = useQuery({
    queryKey: ['student-badges'],
    queryFn: () => gamificationService.getStudentBadges(),
  })

  const { data: challenges, isLoading: challengesLoading } = useQuery({
    queryKey: ['student-challenges'],
    queryFn: () => gamificationService.getChallenges(),
  })

  const { data: leaderboard, isLoading: leaderboardLoading } = useQuery({
    queryKey: ['class-leaderboard'],
    queryFn: () => gamificationService.getLeaderboard('month'),
  })

  // The endpoint scopes itself to the signed-in student, so these are theirs.
  const { data: submissions } = useQuery({
    queryKey: ['student-challenge-submissions'],
    queryFn: () => gamificationService.getChallengeSubmissions(),
  })

  const completedChallenges = useMemo(
    () => new Set((submissions ?? []).filter(s => s.is_approved).map(s => s.challenge)),
    [submissions],
  )

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Target },
    { id: 'badges', name: 'Badges', icon: Award },
    { id: 'challenges', name: 'Challenges', icon: Trophy },
    { id: 'leaderboard', name: 'Leaderboard', icon: Users },
    { id: 'progress', name: 'Progress', icon: TrendingUp }
  ]

  const getLevelProgress = () => {
    if (!pointsData) return 0
    const currentLevelExp = pointsData.experience % 1000
    return (currentLevelExp / 1000) * 100
  }

  const getNextLevelExp = () => {
    if (!pointsData) return 1000
    return 1000 - (pointsData.experience % 1000)
  }

  const getDifficultyColor = (level: number) => {
    switch (level) {
      case 1: return 'text-green-600 bg-green-100'
      case 2: return 'text-yellow-600 bg-yellow-100'
      case 3: return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getBadgeTypeColor = (type: string) => {
    switch (type) {
      case 'academic': return 'bg-blue-100 text-blue-800'
      case 'attendance': return 'bg-green-100 text-green-800'
      case 'sports': return 'bg-orange-100 text-orange-800'
      case 'discipline': return 'bg-purple-100 text-purple-800'
      case 'participation': return 'bg-pink-100 text-pink-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Loading state
  if (pointsLoading || badgesLoading || challengesLoading || leaderboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading gamification data...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (pointsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold">Error loading gamification data</div>
          <p className="text-gray-600 mt-2">Please try refreshing the page</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">My Progress</h1>
            <p className="text-blue-100 mt-2">Track your achievements and compete with classmates</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{pointsData?.total_points || 0}</div>
            <div className="text-blue-100">Total Points</div>
          </div>
        </div>
      </div>

      {/* Level Progress */}
      {pointsData && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-100 p-3 rounded-full">
                <Crown className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Level {pointsData.level}</h3>
                <p className="text-gray-600">{getNextLevelExp()} XP to next level</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{pointsData.streak_days}</div>
              <div className="text-sm text-gray-600">Day Streak</div>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${getLevelProgress()}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Points Breakdown */}
      {pointsData && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-600">{pointsData.academic_points}</div>
            <div className="text-sm text-gray-600">Academic</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-600">{pointsData.attendance_points}</div>
            <div className="text-sm text-gray-600">Attendance</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <CheckCircle className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-purple-600">{pointsData.behavior_points}</div>
            <div className="text-sm text-gray-600">Behavior</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <Zap className="w-8 h-8 text-pink-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-pink-600">{pointsData.participation_points}</div>
            <div className="text-sm text-gray-600">Participation</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <Trophy className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-orange-600">{pointsData.sports_points}</div>
            <div className="text-sm text-gray-600">Sports</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Recent Achievements */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Recent Achievements</h3>
                  <div className="space-y-3">
                    {badges?.filter(award => award.earned_date).slice(0, 3).map((award) => (
                      <div key={award.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl">{award.badge.icon}</div>
                        <div className="flex-1">
                          <div className="font-medium">{award.badge.name}</div>
                          <div className="text-sm text-gray-600">{award.badge.description}</div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(award.earned_date!).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active Challenges */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Active Challenges</h3>
                  <div className="space-y-3">
                    {challenges?.filter(challenge => !completedChallenges.has(challenge.id)).slice(0, 3).map((challenge) => (
                      <div key={challenge.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{challenge.title}</div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(challenge.difficulty_level)}`}>
                            {challenge.difficulty_level === 1 ? 'Easy' : challenge.difficulty_level === 2 ? 'Medium' : 'Hard'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">{challenge.description}</div>
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-green-600">+{challenge.points_reward} points</div>
                          <div className="text-sm text-gray-500">
                            Due: {new Date(challenge.end_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {badges?.map((studentBadge) => (
                  <div key={studentBadge.id} className={`p-4 rounded-lg border-2 ${
                    studentBadge.earned_date ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="text-center">
                      <div className="text-4xl mb-2">{studentBadge.badge.icon}</div>
                      <h4 className="font-semibold mb-1">{studentBadge.badge.name}</h4>
                      <p className="text-sm text-gray-600 mb-3">{studentBadge.badge.description}</p>
                      <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getBadgeTypeColor(studentBadge.badge.badge_type)}`}>
                        {studentBadge.badge.badge_type}
                      </div>
                      {studentBadge.earned_date && (
                        <div className="mt-2 text-xs text-green-600 font-medium">
                          Earned: {new Date(studentBadge.earned_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Challenges Tab */}
          {activeTab === 'challenges' && (
            <div className="space-y-4">
              {challenges?.map((challenge) => (
                <div key={challenge.id} className={`p-4 rounded-lg border ${
                  completedChallenges.has(challenge.id) ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {completedChallenges.has(challenge.id) ? '✅' : '🎯'}
                      </div>
                      <div>
                        <h4 className="font-semibold">{challenge.title}</h4>
                        <p className="text-sm text-gray-600">{challenge.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">+{challenge.points_reward}</div>
                      <div className="text-sm text-gray-500">points</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(challenge.difficulty_level)}`}>
                      {challenge.difficulty_level === 1 ? 'Easy' : challenge.difficulty_level === 2 ? 'Medium' : 'Hard'}
                    </span>
                    <div className="text-sm text-gray-500">
                      {new Date(challenge.start_date).toLocaleDateString()} - {new Date(challenge.end_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Class Leaderboard</h3>
              <div className="space-y-2">
                {leaderboard?.map((entry, index) => (
                  <div key={entry.student} className={`flex items-center justify-between p-4 rounded-lg ${
                    entry.student_name === 'You' ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center space-x-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900' :
                        index === 1 ? 'bg-gray-300 text-gray-700' :
                        index === 2 ? 'bg-orange-400 text-orange-900' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium">{entry.student_name}</div>
                        <div className="text-sm text-gray-600">{entry.student_class}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{entry.points} pts</div>
                      <div className="text-sm text-gray-600">Rank {entry.rank}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-4">Your Progress This Month</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">15</div>
                    <div className="text-sm text-gray-600">Homework Completed</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">95%</div>
                    <div className="text-sm text-gray-600">Attendance Rate</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">3</div>
                    <div className="text-sm text-gray-600">Badges Earned</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
